import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../firebase";
import "./analytics.css";

/**
 * Website traffic — who came, what they did, and where they stopped.
 *
 * Reads three collections the website writes (src/Functions/analytics.js):
 *
 *   Analytics_Daily     one document per day holding every counter. Each
 *                       number on this page except the live feed comes from
 *                       here, so a month of history costs thirty reads rather
 *                       than the hundred thousand events behind it.
 *
 *   Analytics_Events    the raw events, newest first, for the live feed.
 *                       Ordered on one field so no composite index is needed.
 *
 *   Analytics_Visitors  a contact against a visitor id, where the visitor gave
 *                       us one. Fetched only for the ids on screen.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const FEED_LIMIT = 80;

/** The shop's day, matching how the website stamps its events. */
const dayKey = (date) => new Date(date.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);

const lastDays = (count) => {
    const out = [];
    const now = Date.now();
    for (let i = count - 1; i >= 0; i -= 1) out.push(dayKey(new Date(now - i * DAY_MS)));
    return out;
};

const RANGES = [
    { key: "today", label: "Today", days: 1 },
    { key: "week", label: "7 days", days: 7 },
    { key: "month", label: "30 days", days: 30 },
];

const EVENT_LABEL = {
    page_view: "Viewed a page",
    add_to_cart: "Added to cart",
    checkout_start: "Started checkout",
    payment_success: "Paid",
    payment_failed: "Payment failed",
    payment_abandoned: "Closed payment",
    order_placed: "Order placed",
    order_failed: "Order not saved",
    enquiry: "Sent an enquiry",
    appointment: "Booked an appointment",
};

const EVENT_TONE = {
    order_placed: "good",
    payment_success: "good",
    add_to_cart: "good",
    payment_failed: "bad",
    order_failed: "bad",
    payment_abandoned: "warn",
};

/**
 * The steps in order. Each is a strict subset of the one above it, so the gap
 * between two rows is the people who left at that step.
 */
const FUNNEL = [
    ["page_view", "Visited"],
    ["add_to_cart", "Added to cart"],
    ["checkout_start", "Started checkout"],
    ["payment_success", "Paid"],
    ["order_placed", "Order saved"],
];

/** Words that name a page rather than identify one. */
const SEGMENT_LABEL = {
    home: "Home",
    cart: "Cart",
    custom: "Customiser",
    only_kurta: "Kurta",
    only_coat: "Coat",
    only_sadri: "Sadri",
    only_pant: "Trousers",
    select_fabric: "Select fabric",
    select_cloth: "Select cloth",
    select_style: "Select style",
    personalize: "Personalise",
    measurement: "Measurements",
    measurements: "Measurements",
    saved: "Saved",
    select: "Select",
    checkout: "Checkout",
    order: "Order",
    orders: "My orders",
    blog: "Blog",
    about: "About",
    contact: "Contact",
    fabric: "Fabric",
    fabrics: "Fabrics",
    presets: "Presets",
    profile: "Profile",
    login: "Sign in",
};

const titleCase = (word) =>
    word.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const prettySegment = (seg) => {
    if (!seg) return "";
    if (seg === ":id") return "";
    return SEGMENT_LABEL[seg] || titleCase(seg);
};

/**
 * A counter key such as `custom_only_kurta_select_cloth` read back as
 * "Customiser · Kurta · Select cloth". The raw key stays as the tooltip.
 */
const prettyKey = (key) => {
    const parts = String(key || "")
        .split("_:id")
        .join("_")
        .split("_")
        .filter(Boolean);
    // Multi-word segments were joined with the same underscore, so rebuild
    // against the known vocabulary rather than splitting blindly.
    const out = [];
    for (let i = 0; i < parts.length; i += 1) {
        const pair = `${parts[i]}_${parts[i + 1]}`;
        const triple = `${pair}_${parts[i + 2]}`;
        if (SEGMENT_LABEL[triple]) { out.push(SEGMENT_LABEL[triple]); i += 2; continue; }
        if (SEGMENT_LABEL[pair]) { out.push(SEGMENT_LABEL[pair]); i += 1; continue; }
        const label = prettySegment(parts[i]);
        if (label) out.push(label);
    }
    return out.length ? out.join(" · ") : String(key || "—");
};

/** The same treatment for a live URL, whose ids are still in it. */
const prettyPath = (path) => {
    const segments = String(path || "/").split("/").filter(Boolean);
    if (!segments.length) return "Home";
    const out = [];
    for (let i = 0; i < segments.length; i += 1) {
        const seg = segments[i];
        // Deliberately the same rule the website uses to build its counter
        // keys (looksLikeId in src/Functions/analytics.js). If the two drift,
        // the live feed and the top-pages list stop naming the same page the
        // same way. A hyphen is the tell that something is a written slug.
        const isId = /^\d+$/.test(seg)
            || (/[a-z]/.test(seg) && /[A-Z]/.test(seg))
            || (/\d/.test(seg) && /[a-z]/i.test(seg) && seg.indexOf("-") === -1)
            || (seg.length > 24 && seg.indexOf("-") === -1);
        if (isId) continue;
        out.push(SEGMENT_LABEL[seg] || titleCase(seg));
    }
    return out.length ? out.join(" · ") : "Home";
};

const num = (value) => (typeof value === "number" && Number.isFinite(value) ? value : 0);
const money = (value) => `₹${num(value).toLocaleString("en-IN")}`;
const count = (value) => num(value).toLocaleString("en-IN");

/** Sum a map field (byEvent, bySource…) across the days in range. */
const mergeMaps = (docs, field) => {
    const out = {};
    docs.forEach((doc) => {
        const map = doc && doc[field];
        if (!map || typeof map !== "object") return;
        Object.keys(map).forEach((key) => {
            out[key] = num(out[key]) + num(map[key]);
        });
    });
    return out;
};

const topOf = (map, limit) =>
    Object.keys(map)
        .map((key) => [key, num(map[key])])
        .filter(([, value]) => value > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit);

const timeAgo = (date) => {
    if (!date) return "";
    const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
    return `${Math.round(seconds / 86400)}d`;
};

const DEVICE_ICON = { mobile: "Mobile", tablet: "Tablet", desktop: "Desktop", unknown: "—" };

/** A short, stable handle. The visitor id itself is meaningless to read. */
const visitorTag = (id) => (id ? String(id).replace(/-/g, "").slice(-4).toUpperCase() : "????");

/** Two letters for the avatar: initials where we know a name, else the tag. */
const initials = (person, id) => {
    const name = person && person.name;
    if (name) {
        const parts = String(name).trim().split(/\s+/);
        return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
    }
    const email = person && person.email;
    if (email) return String(email).slice(0, 2).toUpperCase();
    return visitorTag(id).slice(0, 2);
};

function Sparkline({ values }) {
    if (values.length < 2) return null;
    const peak = Math.max(1, ...values);
    const step = 100 / (values.length - 1);
    const points = values.map((v, i) => `${(i * step).toFixed(2)},${(28 - (v / peak) * 26).toFixed(2)}`);
    return (
        <svg className="an-spark" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden="true">
            <polyline points={`0,28 ${points.join(" ")} 100,28`} className="an-spark-fill" />
            <polyline points={points.join(" ")} className="an-spark-line" />
        </svg>
    );
}

function Bars({ days, docs, metric }) {
    const values = days.map((day) => ({ day, value: docs[day] ? num(docs[day][metric]) : 0 }));
    const peak = Math.max(1, ...values.map((v) => v.value));

    return (
        <div className="an-bars" role="img" aria-label={`${metric} per day`}>
            {values.map(({ day, value }) => (
                <div className="an-bar-slot" key={day} title={`${day} — ${value}`}>
                    <span className="an-bar-value">{value || ""}</span>
                    <div className="an-bar-track">
                        {/* Zero still needs a floor, or an empty day reads as a missing one. */}
                        <div className="an-bar-fill" style={{ height: value ? `${Math.max(5, (value / peak) * 100)}%` : "2px" }} />
                    </div>
                    <span className="an-bar-day">{day.slice(8)}</span>
                </div>
            ))}
        </div>
    );
}

function Breakdown({ title, rows, total, empty, format }) {
    return (
        <section className="an-card">
            <h4 className="an-card-title">{title}</h4>
            {rows.length === 0 ? (
                <p className="an-empty">{empty}</p>
            ) : (
                <ul className="an-list">
                    {rows.map(([key, value]) => (
                        <li key={key}>
                            <span className="an-list-label" title={key}>{format ? format(key) : key}</span>
                            <span className="an-list-bar">
                                <span style={{ width: `${total ? Math.max(2, (value / total) * 100) : 0}%` }} />
                            </span>
                            <span className="an-list-count">{count(value)}</span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

export default function AnalyticsPage() {
    const [rangeKey, setRangeKey] = useState("today");
    const [docs, setDocs] = useState({});
    const [feed, setFeed] = useState([]);
    const [people, setPeople] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [, setTick] = useState(0);

    /**
     * Who Firestore thinks is asking.
     *
     * A permission error says nothing about which identity was refused, and
     * these collections are readable only by the whitelisted admin login. Being
     * signed in as the wrong account looks exactly like broken rules from here,
     * so the error says which it is.
     */
    const identity = useMemo(() => {
        try {
            const user = auth.currentUser;
            if (!user) return { signedIn: false };
            return {
                signedIn: true,
                email: user.email || "",
                anonymous: Boolean(user.isAnonymous),
                providers: (user.providerData || []).map((p) => p && p.providerId).filter(Boolean).join(", "),
            };
        } catch (e) {
            return { signedIn: false };
        }
    }, []);

    const range = RANGES.find((r) => r.key === rangeKey) || RANGES[0];
    const days = useMemo(() => lastDays(range.days), [range.days]);

    // The counters: one read per day in range, re-run only when the range moves.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");

        Promise.all(days.map((day) => db.collection("Analytics_Daily").doc(day).get()))
            .then((snaps) => {
                if (cancelled) return;
                const next = {};
                snaps.forEach((snap) => { if (snap.exists) next[snap.id] = snap.data(); });
                setDocs(next);
                setLoading(false);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.message || String(e));
                setLoading(false);
            });

        return () => { cancelled = true; };
    }, [days]);

    // The live feed. Ordered on a single field, so Firestore needs no index.
    useEffect(() => {
        const unsubscribe = db
            .collection("Analytics_Events")
            .orderBy("at", "desc")
            .limit(FEED_LIMIT)
            .onSnapshot(
                (snap) => setFeed(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))),
                (e) => setError(e?.message || String(e)),
            );
        return () => unsubscribe();
    }, []);

    // "2m" would otherwise freeze at whatever it said when it rendered.
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 30000);
        return () => clearInterval(id);
    }, []);

    // Contacts, fetched once per visitor id and only for ids on screen.
    const wanted = useRef(new Set());
    const loadPeople = useCallback((ids) => {
        const fresh = ids.filter((id) => id && !wanted.current.has(id));
        if (!fresh.length) return;
        fresh.forEach((id) => wanted.current.add(id));

        fresh.forEach((id) => {
            db.collection("Analytics_Visitors").doc(id).get()
                .then((snap) => {
                    if (!snap.exists) return;
                    setPeople((prev) => ({ ...prev, [id]: snap.data() }));
                })
                .catch(() => {
                    // A missing contact is the normal case, not a failure.
                });
        });
    }, []);

    useEffect(() => {
        loadPeople([...new Set(feed.map((e) => e.visitorId).filter(Boolean))]);
    }, [feed, loadPeople]);

    const list = useMemo(() => days.map((day) => docs[day]).filter(Boolean), [days, docs]);

    const totals = useMemo(() => {
        const sum = (field) => list.reduce((acc, doc) => acc + num(doc[field]), 0);
        return {
            visitors: sum("newVisitors"),
            sessions: sum("sessions"),
            pageViews: sum("pageViews"),
            orders: sum("orders"),
            revenue: sum("revenue"),
            events: sum("events"),
        };
    }, [list]);

    const byEvent = useMemo(() => mergeMaps(list, "byEvent"), [list]);
    const bySource = useMemo(() => mergeMaps(list, "bySource"), [list]);
    const byDevice = useMemo(() => mergeMaps(list, "byDevice"), [list]);
    const byPath = useMemo(() => mergeMaps(list, "byPath"), [list]);

    const series = useCallback(
        (metric) => days.map((day) => (docs[day] ? num(docs[day][metric]) : 0)),
        [days, docs],
    );

    const funnel = useMemo(() => {
        // Measured against arrivals, so each row reads as a share of everyone
        // who came rather than of the row directly above it.
        const first = num(byEvent[FUNNEL[0][0]]);
        return FUNNEL.map(([key, label]) => {
            const value = num(byEvent[key]);
            return { key, label, value, share: first ? (value / first) * 100 : 0 };
        });
    }, [byEvent]);

    const liveVisitors = useMemo(() => {
        const cutoff = Date.now() - 5 * 60 * 1000;
        const seen = new Set();
        feed.forEach((event) => {
            const at = event.at && event.at.toDate ? event.at.toDate() : null;
            if (at && at.getTime() >= cutoff && event.visitorId) seen.add(event.visitorId);
        });
        return seen.size;
    }, [feed]);

    /**
     * One row per visitor in the feed, newest first — the shop's follow-up
     * list. The feed says what happened; this says who it happened to.
     */
    const visitors = useMemo(() => {
        const byId = new Map();
        feed.forEach((event) => {
            const id = event.visitorId;
            if (!id) return;
            const at = event.at && event.at.toDate ? event.at.toDate() : null;
            const existing = byId.get(id);
            if (!existing) {
                byId.set(id, { id, last: at, actions: 1, latest: event, device: event.device, reached: event.name });
                return;
            }
            existing.actions += 1;
            // Events arrive newest first, so the furthest step is whichever of
            // these ranks highest — not simply the last one seen.
            const rank = (name) => FUNNEL.findIndex(([key]) => key === name);
            if (rank(event.name) > rank(existing.reached)) existing.reached = event.name;
        });
        return [...byId.values()].sort((a, b) => (b.last?.getTime() || 0) - (a.last?.getTime() || 0));
    }, [feed]);

    const sourceTotal = Object.values(bySource).reduce((a, b) => a + num(b), 0);
    const deviceTotal = Object.values(byDevice).reduce((a, b) => a + num(b), 0);
    const pathTotal = Object.values(byPath).reduce((a, b) => a + num(b), 0);

    const nothingYet = !loading && !error && totals.events === 0 && feed.length === 0;

    const kpis = [
        { label: "New visitors", value: count(totals.visitors), hint: "First time here", metric: "newVisitors" },
        { label: "Visits", value: count(totals.sessions), hint: "Sessions", metric: "sessions" },
        { label: "Page views", value: count(totals.pageViews), hint: "Pages opened", metric: "pageViews" },
        { label: "Orders", value: count(totals.orders), hint: "Saved orders", metric: "orders" },
        { label: "Revenue", value: money(totals.revenue), hint: "From those orders", metric: "revenue" },
    ];

    return (
        <div className="an-wrap">
            <header className="an-head">
                <div className="an-head-left">
                    <p className="an-kicker">Website</p>
                    <h3 className="an-title">Who is on the site</h3>
                    <p className="an-sub">
                        Live activity, and where visitors stop before they order.
                    </p>
                </div>
                <div className="an-head-right">
                    {liveVisitors > 0 && (
                        <span className="an-live">
                            <span className="an-live-dot" />
                            {liveVisitors} online
                        </span>
                    )}
                    <div className="an-range">
                        {RANGES.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                className={r.key === rangeKey ? "is-on" : ""}
                                onClick={() => setRangeKey(r.key)}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            {error && (
                <div className="an-error">
                    <strong>Could not load the data.</strong> {error}
                    <span className="an-error-hint">
                        Signed in as{" "}
                        <code>
                            {identity.signedIn
                                ? `${identity.email || "(no email)"}${identity.anonymous ? " · anonymous" : ""}${identity.providers ? ` · ${identity.providers}` : ""}`
                                : "nobody"}
                        </code>
                    </span>
                    <span className="an-error-hint">
                        These collections are readable only by the admin accounts listed in
                        firestore.rules. If the address above is not one of them, sign out and
                        back in with the one that is.
                    </span>
                </div>
            )}

            {nothingYet && (
                <div className="an-empty-state">
                    <h4>Nothing recorded yet</h4>
                    <p>
                        Visitors are counted once the new website build is live. This page fills
                        in on its own the moment someone opens the site.
                    </p>
                </div>
            )}

            <div className="an-kpis">
                {kpis.map((kpi) => (
                    <article className="an-kpi" key={kpi.label}>
                        <span className="an-kpi-label">{kpi.label}</span>
                        <strong className="an-kpi-value">{loading ? "—" : kpi.value}</strong>
                        <span className="an-kpi-hint">{kpi.hint}</span>
                        {range.days > 1 && <Sparkline values={series(kpi.metric)} />}
                    </article>
                ))}
            </div>

            <div className="an-cols">
                <section className="an-card an-funnel">
                    <h4 className="an-card-title">
                        How far they get
                        <span className="an-card-sub">{range.label.toLowerCase()}</span>
                    </h4>
                    <p className="an-card-note">
                        Every bar is a share of everyone who arrived. The gap between two bars is
                        the people who stopped there.
                    </p>
                    <ol className="an-funnel-list">
                        {funnel.map((step, i) => {
                            const previous = i > 0 ? funnel[i - 1].value : step.value;
                            const lost = Math.max(0, previous - step.value);
                            return (
                                <li key={step.key}>
                                    <div className="an-funnel-row">
                                        <span className="an-funnel-label">{step.label}</span>
                                        <span className="an-funnel-count">
                                            {count(step.value)}
                                            <em>{step.share.toFixed(step.share > 0 && step.share < 10 ? 1 : 0)}%</em>
                                        </span>
                                    </div>
                                    <div className="an-funnel-track">
                                        <div className="an-funnel-fill" style={{ width: `${step.share}%` }} />
                                    </div>
                                    {i > 0 && lost > 0 && (
                                        <span className={`an-funnel-lost${step.key === "order_placed" ? " is-critical" : ""}`}>
                                            {step.key === "order_placed"
                                                ? `${count(lost)} paid but the order did not save`
                                                : `${count(lost)} stopped here`}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                </section>

                <section className="an-card an-feed">
                    <h4 className="an-card-title">
                        Live activity
                        <span className="an-card-sub">{feed.length ? `last ${feed.length}` : ""}</span>
                    </h4>
                    {feed.length === 0 ? (
                        <p className="an-empty">Nothing happening right now.</p>
                    ) : (
                        <ul className="an-feed-list">
                            {feed.map((event) => {
                                const at = event.at && event.at.toDate ? event.at.toDate() : null;
                                const tone = EVENT_TONE[event.name] || "";
                                const person = people[event.visitorId];
                                return (
                                    <li key={event.id} className={tone ? `is-${tone}` : ""}>
                                        <span className="an-avatar" title={event.visitorId || ""}>
                                            {initials(person, event.visitorId)}
                                        </span>
                                        <span className="an-feed-body">
                                            <span className="an-feed-top">
                                                <span className="an-feed-who">
                                                    {person?.name || person?.email || `Visitor ${visitorTag(event.visitorId)}`}
                                                </span>
                                                <span className="an-feed-act">{EVENT_LABEL[event.name] || event.name}</span>
                                            </span>
                                            <span className="an-feed-where" title={event.path}>
                                                {prettyPath(event.path)}
                                            </span>
                                            {person && (person.phone || person.email) && (
                                                <span className="an-feed-contact">
                                                    {person.phone && (
                                                        <a href={`tel:${person.phone}`}>{person.phone}</a>
                                                    )}
                                                    {person.phone && person.email && <i>·</i>}
                                                    {person.email && (
                                                        <a href={`mailto:${person.email}`}>{person.email}</a>
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                        <span className="an-feed-meta">
                                            <em>{timeAgo(at)}</em>
                                            {DEVICE_ICON[event.device] || ""}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            </div>

            <section className="an-card an-people">
                <h4 className="an-card-title">
                    Recent visitors
                    <span className="an-card-sub">{visitors.length ? `${visitors.length} in view` : ""}</span>
                </h4>
                <p className="an-card-note">
                    A contact appears once the visitor has given us one — in an enquiry, an
                    appointment or at checkout. The rest stay anonymous.
                </p>
                {visitors.length === 0 ? (
                    <p className="an-empty">No visitors yet.</p>
                ) : (
                    <div className="an-table-scroll">
                        <table className="an-table">
                            <thead>
                                <tr>
                                    <th>Visitor</th>
                                    <th>Contact</th>
                                    <th>Furthest step</th>
                                    <th className="is-num">Actions</th>
                                    <th>Device</th>
                                    <th className="is-num">Last seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visitors.map((v) => {
                                    const person = people[v.id];
                                    const step = FUNNEL.find(([key]) => key === v.reached);
                                    return (
                                        <tr key={v.id}>
                                            <td>
                                                <span className="an-person">
                                                    <span className="an-avatar is-sm">{initials(person, v.id)}</span>
                                                    {person?.name || `Visitor ${visitorTag(v.id)}`}
                                                </span>
                                            </td>
                                            <td>
                                                {person?.phone || person?.email ? (
                                                    <span className="an-contact">
                                                        {person.phone && <a href={`tel:${person.phone}`}>{person.phone}</a>}
                                                        {person.email && <a href={`mailto:${person.email}`}>{person.email}</a>}
                                                    </span>
                                                ) : (
                                                    <span className="an-none">Not given</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`an-step${EVENT_TONE[v.reached] ? ` is-${EVENT_TONE[v.reached]}` : ""}`}>
                                                    {step ? step[1] : EVENT_LABEL[v.reached] || v.reached}
                                                </span>
                                            </td>
                                            <td className="is-num">{count(v.actions)}</td>
                                            <td>{DEVICE_ICON[v.device] || "—"}</td>
                                            <td className="is-num">{timeAgo(v.last)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {range.days > 1 && (
                <section className="an-card an-chart">
                    <h4 className="an-card-title">Visits per day</h4>
                    <Bars days={days} docs={docs} metric="sessions" />
                </section>
            )}

            <div className="an-cols an-cols-3">
                <Breakdown
                    title="Most viewed pages"
                    rows={topOf(byPath, 8)}
                    total={pathTotal}
                    format={prettyKey}
                    empty="No page views yet."
                />
                <Breakdown
                    title="Where they came from"
                    rows={topOf(bySource, 8)}
                    total={sourceTotal}
                    format={(key) => (key === "direct" ? "Direct" : key === "internal" ? "Within the site" : key)}
                    empty="No visitors yet."
                />
                <Breakdown
                    title="Device"
                    rows={topOf(byDevice, 5)}
                    total={deviceTotal}
                    format={(key) => DEVICE_ICON[key] || titleCase(key)}
                    empty="No visitors yet."
                />
            </div>
        </div>
    );
}
